import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PendingPagePropsType } from './types';
import {
  DateContainer,
  DateTitle,
  JobLine,
  NoPendingMessage,
  PendingPageContainer,
  PendingPageContent,
} from './elements';
import { REFETCH_PERIOD, RUNNING_JOB_STATUSES } from '../config';
import { doGetJobs } from 'common/state/jobs/thunkActions';
import { Job } from 'common/state/jobs/actions';
import { GroupedJobsType } from '../calendar/types';
import { StatusIcon } from '../statusIcon';

export const PendingPage = ({
  jobs,
  jobsDispatch,
  setModalMode,
  setFocusedJob,
}: PendingPagePropsType) => {
  const [prevIds, setPrevIds] = useState<number[]>([]);
  const refetchInterval = useRef<ReturnType<typeof setInterval>>();

  const fetchJobs = useCallback(() => {
    jobsDispatch(
      doGetJobs({
        status: Array.from(RUNNING_JOB_STATUSES),
      })
    );
    if (prevIds.length)
      jobsDispatch(
        doGetJobs({
          id: prevIds,
        })
      );
    const newIds = Object.values(jobs)
      ?.filter((job: Job) => RUNNING_JOB_STATUSES.has(job.status))
      .map((job) => job.id);
    setPrevIds(newIds);
  }, [jobs, prevIds, jobsDispatch]);

  useEffect(() => {
    if (!refetchInterval.current) fetchJobs();
    clearInterval(refetchInterval.current);
    refetchInterval.current = setInterval(fetchJobs, REFETCH_PERIOD);
    return () => clearInterval(refetchInterval.current);
  }, [fetchJobs]);

  const groupedJobIds = useMemo(
    (): GroupedJobsType =>
      Object.values(jobs)
        ?.filter((job: Job) => RUNNING_JOB_STATUSES.has(job.status))
        ?.sort((jobA: Job, jobB: Job) => {
          if (jobA.started > jobB.started) return 1;
          if (jobA.started < jobB.started) return -1;
          return 0;
        })
        ?.reduce((acc: GroupedJobsType, job: Job) => {
          const dateString = job.params.date.slice(0, 10);
          if (!acc[dateString]) acc[dateString] = [];
          acc[dateString].push(job.id);
          return acc;
        }, {}),
    // eslint-disable-next-line
    [jobs]
  );

  const sortedDates = useMemo(
    () => Object.keys(groupedJobIds).sort(),
    [groupedJobIds]
  );

  return (
    <PendingPageContainer>
      <PendingPageContent>
        {sortedDates.length ? (
          sortedDates.map((dateString: string) => (
            <DateContainer key={dateString}>
              <DateTitle>
                Jobs for {new Date(dateString).toLocaleDateString()}
              </DateTitle>
              {groupedJobIds[dateString].map((jobId: number) => {
                const job = jobs[jobId];
                return (
                  <JobLine
                    key={jobId}
                    onClick={() => {
                      setFocusedJob(job.id);
                      setModalMode('job');
                    }}
                  >
                    <StatusIcon status={job.status} size={1} />
                    <div>
                      {job.type
                        .replaceAll('_', ' ')
                        .split(' ')
                        .map((word) => word[0].toUpperCase() + word.slice(1))
                        .join(' ')}
                      {' '}
                      ({job.id})
                    </div>
                    <div>
                      {job.started &&
                        `Duration: ${Math.round((Date.now() - new Date(job.started).getTime()) / 60000)} minutes`}
                    </div>
                    <div>
                      {job.started &&
                        `Started: ${new Date(job.started).toLocaleString()}`}
                    </div>
                  </JobLine>
                );
              })}
            </DateContainer>
          ))
        ) : (
          <NoPendingMessage>No pending jobs </NoPendingMessage>
        )}
      </PendingPageContent>
    </PendingPageContainer>
  );
};
